import { type Field, type TypeInstanceMap, type VariableADTvalue, VariableType } from '@lighthouse/core'
import type { FlowNode, WechatTemplateMsgActionConfig } from '@lighthouse/shared'
import {
    addresseeFieldType,
    getAllCanBeUpstreamNodes,
    getUpstreamNodesOptions,
    getUserDatasourceOptions,
    innerTypeToFieldTypeMapInCondition,
    PAGE_LINK_OPTION,
    useApplicationContext,
    useAtomData,
    USER_DATASOURCE
} from '@lighthouse/shared'
import { useNodesData } from '@xyflow/react'
import { find, includes } from 'rambda'
import React, { useCallback, useMemo } from 'react'

import { otherPlatformListAtom } from '@/atoms/application/state'
import { lastPageOfStackAtom } from '@/atoms/page/state'
import { workflowListAtom } from '@/atoms/workflow/state'
import { useFlow, useFlowContainerContext } from '@/contexts/FlowContext'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useGetPageParameterAndVariableOptions, useGetUserRecordOptions } from '@/hooks/usePage'
import { useGetWxMessageInfo } from '@/shared/reusable'

import { unitList } from '../../../NodeSelector/constants'
import * as CM from '../styles'
import { extractDataFn, textRichVariableName } from '../util'
import * as SC from './styles'

type WeChatTemplateMsgCardProps = {
    config: WechatTemplateMsgActionConfig
}
const WeChatTemplateMsgCard = ({ config }: WeChatTemplateMsgCardProps) => {
    const { personList, templateId } = config
    const otherPlatformList = useAtomData(otherPlatformListAtom)

    const { type } = useFlowContainerContext()
    const isNotAutomation = type !== 'automation'
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const workflowList = useAtomData(workflowListAtom)
    const { personOptions, roleOptions, departmentOptions } = useApplicationContext()
    const { data } = useGetWxMessageInfo(appId)

    const [stackId, pageId] = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => [s?.stackId || '', s?.pageId || ''], [])
    )

    const pageOptions = useGetPageParameterAndVariableOptions({ pageId })

    const nodeIdsAddressee = useMemo(() => {
        const arr: string[] = []
        if (personList.length) {
            personList.forEach(item => {
                if (item.type === VariableType.UPSTREAM) {
                    const nodeId = item?.upstreamVariable?.nodeId
                    if (nodeId) {
                        arr.push(nodeId)
                    }
                }
            })
        }
        return arr
    }, [personList])

    const nodesAddressee = useNodesData(nodeIdsAddressee) as FlowNode[]

    const userOptionAddressee = useMemo(() => {
        const userDataSource = find(item => item.id === USER_DATASOURCE, dataSourceList)
        return getUserDatasourceOptions({
            dataSource: userDataSource
        })
    }, [dataSourceList])

    const upstreamOptionsAddressee = useMemo(
        () =>
            getUpstreamNodesOptions({
                workflowList,
                upstreamNodes: nodesAddressee || [],
                allUpstreamNodes: nodesAddressee || [],
                dataSourceList
            }),
        [dataSourceList, nodesAddressee, workflowList]
    )

    const textList = useMemo(() => {
        const arr: { label: string; value: string }[] = []
        personList.forEach(item => {
            if (item.type === VariableType.VALUE) {
                const valueUserId =
                    item.valueVariable?.value && Array.isArray(item.valueVariable?.value) ? item.valueVariable?.value?.[0] : ''
                if (item?.valueVariable?.type === 'role') {
                    const role = find(item => item.id === valueUserId, roleOptions || [])
                    arr.push({
                        label: '角色',
                        value: role?.name || ''
                    })
                } else if (item?.valueVariable?.type === 'department') {
                    const department = find(item => item.id === valueUserId, departmentOptions || [])
                    arr.push({
                        label: '部门',
                        value: department?.name || ''
                    })
                } else {
                    const person = find(item => item.id === valueUserId, personOptions || [])
                    if (valueUserId === '{currentUserId}') {
                        arr.push({
                            label: '成员',
                            value: '当前用户'
                        })
                    } else {
                        arr.push({
                            label: '成员',
                            value: person?.name || ''
                        })
                    }
                }
            } else if (item.type === VariableType.USER || item.type === VariableType.UPSTREAM) {
                const val = textRichVariableName({
                    userOption: userOptionAddressee,
                    upstreamOptions: upstreamOptionsAddressee,
                    pageOptions: isNotAutomation ? [PAGE_LINK_OPTION, ...pageOptions] : undefined,
                    data: item as VariableADTvalue
                })
                arr.push({
                    label: '',
                    value: val || ''
                })
            }
        })
        return arr
    }, [
        personList,
        userOptionAddressee,
        departmentOptions,
        roleOptions,
        personOptions,
        isNotAutomation,
        pageOptions,
        upstreamOptionsAddressee
    ])

    const templateTitle = useMemo(() => {
        if (data) {
            const template = find(item => item.templateId === templateId, data)
            return template?.title || ''
        }
        return ''
    }, [data, templateId])

    const wechatAccount = useMemo(() => otherPlatformList?.find(item => item.type === 'WECHAT_OFFICIAL_ACCOUNT'), [otherPlatformList])

    if (!wechatAccount?.configurationComplete) {
        return null
    }

    return (
        <>
            {textList && textList.length > 0 && (
                <>
                    <SC.TypeName>收件人</SC.TypeName>
                    <CM.FieldList>
                        {textList.map((item, index) => (
                            <CM.FieldItem key={index}>
                                {item.label ? (
                                    <>
                                        <CM.FieldItemTitle>{item.value}</CM.FieldItemTitle>
                                        <SC.FieldItemRightTitle>{item.label}</SC.FieldItemRightTitle>
                                    </>
                                ) : (
                                    <CM.FieldItemTitle color="var(--color-purple-500)">{item.value}</CM.FieldItemTitle>
                                )}
                            </CM.FieldItem>
                        ))}
                    </CM.FieldList>
                </>
            )}

            {templateTitle && (
                <>
                    <SC.TypeName>消息模板</SC.TypeName>
                    <CM.FieldItemContent>
                        <CM.FieldItemTitle>{templateTitle}</CM.FieldItemTitle>
                    </CM.FieldItemContent>
                </>
            )}
        </>
    )
}

export default WeChatTemplateMsgCard
